import { useEffect, useState } from 'react'
import { getESPTitle } from 'app/IntegrationDrawer'

import breakpoints from 'common/breakpoints'
import { useAppDispatch, useAppSelector } from 'common/hooks/redux'
import { useWindowDimensions } from 'common/hooks/useWindowDimensions'
import {
  hideRightPanel,
  selectRightPanelVisibility,
  showRightPanel,
} from 'features/unifiedFlow/store/unifiedFlowSlice'

import CampaignLogs from '../../components/CampaignLogs'
import RightPanel from '../../components/layout/RightPanel'
import { getContentHasVariants, getIsOptimized } from '../../helpers'
import { useContent, useSelectedElement } from '../../hooks'

import CollapsedIcons from './CollapsedIcons'
import ExpandedIcons from './ExpandedIcons'
import IntegrationLogSection from './IntegrationLogSection'
import OptimizeIntegrationSection, {
  parseIntegrationType,
} from './OptimizeIntegrationSection'

export type ActivePanel =
  | 'integrationLogs'
  | 'optimizeIntegration'
  | 'campaignLogs'
export type HandleActivePanelChange = (panel: ActivePanel) => void

const RightSection = () => {
  const { screenWidth } = useWindowDimensions()
  const dispatch = useAppDispatch()
  const [activePanel, setActivePanel] = useState<ActivePanel | undefined>(
    undefined
  )

  const { content } = useContent()
  const { data: selectedElement } = useSelectedElement()
  const rightPanelVisibility = useAppSelector(selectRightPanelVisibility)

  const campaignData = useAppSelector(
    (state) => state.campaignStates.campaignData
  )

  const isOptimized = getIsOptimized(selectedElement)
  const hasVariants = getContentHasVariants(content)

  const integrationTypeParsed = parseIntegrationType(campaignData)
  const integrationTitle = getESPTitle(integrationTypeParsed)

  const handleActivePanelChange: HandleActivePanelChange = (
    panel: ActivePanel
  ) => {
    if (panel === activePanel) {
      setActivePanel(undefined)
      dispatch(hideRightPanel())
    } else {
      setActivePanel(panel)

      if (rightPanelVisibility !== 'expanded') {
        dispatch(showRightPanel())
      }
    }
  }

  useEffect(() => {
    setActivePanel(undefined)

    if (!selectedElement) {
      dispatch(hideRightPanel({ visibility: 'none' }))
    } else if (
      isOptimized &&
      !!integrationTitle &&
      screenWidth > breakpoints.lg
    ) {
      setActivePanel('optimizeIntegration')
      dispatch(showRightPanel())
    } else {
      dispatch(hideRightPanel({ visibility: 'collapsed' }))
    }
    // Do not run on resize
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, selectedElement, isOptimized, hasVariants])

  return (
    <RightPanel
      collapsedIcons={
        <CollapsedIcons
          integrationTitle={integrationTitle}
          handleActivePanelChange={handleActivePanelChange}
        />
      }
      onOutsideClick={() => {
        setActivePanel(undefined)
        dispatch(hideRightPanel())
      }}
    >
      {
        {
          campaignLogs: (
            <CampaignLogs
              expandedIcons={
                <ExpandedIcons
                  integrationTitle={integrationTitle}
                  activePanel={activePanel!}
                  handleActivePanelChange={handleActivePanelChange}
                />
              }
            />
          ),
          integrationLogs: (
            <IntegrationLogSection
              activePanel={activePanel!}
              handleActivePanelChange={handleActivePanelChange}
            />
          ),
          optimizeIntegration: (
            <OptimizeIntegrationSection
              activePanel={activePanel!}
              handleActivePanelChange={handleActivePanelChange}
            />
          ),
        }[activePanel || '']
      }
    </RightPanel>
  )
}

export default RightSection
